import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";

interface Option {
  label: string;
  value: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  width?: string;
  searchable?: boolean;
  selectWrapClass?: string;
  required?: boolean;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
}

const FleetrifyCustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select option",
  width = "w-[160px]",
  searchable = true,
  selectWrapClass,
  required = false,
  label,
  loading = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  const handleSingleSelect = (optionValue: string) => {
    onChange(optionValue);
    closeDropdown();
  };

  const openDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 256;


      const shouldDropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setDropUp(shouldDropUp);

      if (shouldDropUp) {
        setPosition({ 
          top: rect.top + window.scrollY - dropdownHeight - 8, 
          left: rect.left, 
          width: rect.width 
        });
      } else {
        setPosition({ 
          top: rect.bottom + window.scrollY + 8, 
          left: rect.left, 
          width: rect.width 
        });
      }
    }
    setIsOpen(true);
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery("");
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const handleUpdate = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const dropdownHeight = 256;

          const shouldDropUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
          setDropUp(shouldDropUp);

          if (shouldDropUp) {
            setPosition({ 
              top: rect.top + window.scrollY - dropdownHeight - 8, 
              left: rect.left, 
              width: rect.width 
            });
          } else {
            setPosition({ 
              top: rect.bottom + window.scrollY + 8, 
              left: rect.left, 
              width: rect.width 
            });
          }
        }
      };

      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [isOpen]);

  const filteredOptions = searchable
    ? options.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    )
    : options;

  return (
    <div className={`relative ${width}`}>
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-700 whitespace-nowrap">
          {label} {required && <span className="text-[#EB5757]">*</span>}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        className={clsx(
          "w-full bg-white border rounded-xl py-2 pl-4 pr-3 text-left text-sm flex justify-between gap-5 items-center whitespace-nowrap overflow-hidden text-ellipsis",
          selectWrapClass,
          required && !value && "border-red-500",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        disabled={disabled}
      >
        {loading ? (
          <span className="flex items-center gap-2 text-gray-400">
            <Loader2 className="animate-spin" size={16} /> Loading...
          </span>
        ) : (
          <span className={clsx("truncate", !value && "text-gray-400")}>{displayLabel}</span>
        )}
        <ChevronDown size={18} className={clsx("transition-transform", isOpen && "rotate-180")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-50"
              onClick={closeDropdown}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              ref={dropdownRef}
              className="fixed z-50 bg-white rounded-xl shadow-lg max-h-64 min-w-56 overflow-auto ring-1 ring-black/10"
              style={{ 
                top: position.top, 
                left: position.left, 
                width: position.width,
                maxHeight: '256px' 
              }}
              initial={{ opacity: 0, y: dropUp ? 10 : -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: dropUp ? 10 : -10 }}
              transition={{ duration: 0.2 }}
            >
              {searchable && !loading && (
                <div className="p-2 sticky top-0 bg-white z-10 border-b">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              {loading ? (
                <div className="px-4 py-3 text-gray-400 flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} /> Loading options...
                </div>
              ) : filteredOptions.length > 0 ? (
                <div className="py-1">
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleSingleSelect(option.value)}
                      className={clsx(
                        "px-4 py-2.5 cursor-pointer hover:bg-blue-50 whitespace-nowrap overflow-hidden text-ellipsis transition-colors text-sm",
                        value === option.value && "bg-blue-100 text-blue-600 font-semibold"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{option.label}</span>
                        {value === option.value && <Check size={16} className="flex-shrink-0" />}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 text-gray-400 text-sm text-center">
                  No results found
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FleetrifyCustomSelect;